import { Spacing } from '@persona-id/design-tokens';
import PlusIcon from '@persona-id/design-tokens/icons/PlusIcon';
import { useCallback } from 'react';
import styled from 'styled-components';

import TagsModal from 'dashboard/components/TagsModal';
import { FormUpdateTagsData } from 'dashboard/forms/FormUpdateTags';
import { Tag, TagScope } from 'dashboard/models/tag';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import Sticker from 'design-system/components/Sticker';
import useToggle from 'lib/hooks/useToggle';

const Container = styled('div')`
  display: flex;
  justify-content: flex-start;
  flex-direction: row;
  flex-wrap: wrap;
`;

const StickerContainer = styled('div')`
  display: flex;
  flex-wrap: wrap;
  gap: ${Spacing[2]};
`;

const ButtonContainer = styled('div')`
  display: flex;
  align-items: center;
  box-sizing: border-box;
`;

interface TagsSummaryProps {
  canEdit?: boolean;
  tags: Tag[];
  tagScope: TagScope;
  handleUpdateTags?: (tags: FormUpdateTagsData) => void;
}

const TagsSummary = ({ tags, tagScope, canEdit = true, handleUpdateTags }: TagsSummaryProps) => {
  const [updateTagsModal, { enable: updateTags, disable: hideUpdateTagsModal }] = useToggle(false);

  const handleUpdateTagsAndClose = useCallback(
    async (tags: FormUpdateTagsData) => {
      await handleUpdateTags?.(tags);
      hideUpdateTagsModal();
    },
    [handleUpdateTags],
  );

  const hasTagsToEdit = canEdit && tags.length > 0 && handleUpdateTags != null;

  return (
    <>
      <Card data-test='tags-summary'>
        <Card.Body>
          <Card.Title
            actions={
              hasTagsToEdit && (
                <ButtonContainer>
                  <Button
                    variant='basic'
                    onClick={updateTags}
                    size='sm'
                    data-test='tags-summary__button--update-tags'
                  >
                    Edit
                  </Button>
                </ButtonContainer>
              )
            }
          >
            Tags
            {tags.length > 0 && <Sticker>{tags.length}</Sticker>}
          </Card.Title>

          <Container>
            <StickerContainer>
              {tags.map((tag) => (
                <Sticker key={tag.id} data-test={`tags-summary__tag--${tag.id}`}>
                  {tag.attributes.name}
                </Sticker>
              ))}
            </StickerContainer>

            {tags.length === 0 && handleUpdateTags != null && (
              <ButtonContainer>
                <Button
                  variant='basic'
                  onClick={updateTags}
                  data-test='tags-summary__button--add-tags'
                  prefixIcon={<PlusIcon />}
                  width='auto'
                >
                  Add tag
                </Button>
              </ButtonContainer>
            )}
          </Container>
        </Card.Body>
      </Card>
      <TagsModal
        tags={tags}
        tagScope={tagScope}
        updateTagsModal={updateTagsModal}
        hideUpdateTagsModal={hideUpdateTagsModal}
        handleUpdateTagsAndClose={handleUpdateTagsAndClose}
      />
    </>
  );
};

export default TagsSummary;
